import { useMemo } from 'react'
import { VersionReportVersionPageEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { ReorderRow } from '../../../../../applications/ports/versionReportVersionRepository'
import { ProgressOverview } from './ProgressOverview'
import { PageHeader, PageRoot } from '.'
import { ProgressReportByProcess } from './ProgressReportByProcess'
import { ProgressReportByTeam } from './ProgressReportByTeam'
import { VersionReportVersionPageFormModel } from '../dialog/VersionConfigDialog/model/VersionReportVersionPageFormModel'
import { StoredVersionReportPageDisplayState } from '../../hooks/pageDisplayState'

type VersionReportPageInput =
  | VersionReportVersionPageEntity
  | VersionReportVersionPageFormModel

type VersionReportPageProps = {
  snapshotUuid: string | undefined
  page: VersionReportPageInput | undefined
  updatePageTitle: (v: string) => void
  updateLeadSentence: (v: string) => void
  displayState: StoredVersionReportPageDisplayState
  updateDisplayState: (
    updated: Partial<StoredVersionReportPageDisplayState>
  ) => void
  updateRow: (
    rowUuid: string,
    lockVersion: number,
    key: 'status' | 'comment',
    value: string
  ) => Promise<boolean>
  applyReorderedRows: (
    pageUuid: string,
    reorderPayload: ReorderRow[]
  ) => Promise<void>
  commitPageContent: (
    pageUuid: string,
    overrides?: { pageTitle?: string; leadSentence?: string | undefined }
  ) => Promise<boolean>
  isPreview?: boolean
  showLoading: () => void
  hideLoading: () => void
  // Redis 復元時のみ増えるトークン。PROCESS の columnState 再適用トリガーに使う。
  displayStateRestoreToken?: number
}
export const VersionReportPage = ({
  snapshotUuid,
  page,
  isPreview,
  updatePageTitle,
  displayState,
  updateDisplayState,
  updateLeadSentence,
  updateRow,
  applyReorderedRows,
  commitPageContent,
  showLoading,
  hideLoading,
  displayStateRestoreToken,
}: VersionReportPageProps) => {
  const pageUuid = page && 'uuid' in page ? page.uuid : null
  const onBlurPageTitle = (v: string): Promise<boolean> => {
    updatePageTitle(v)
    if (!pageUuid) return Promise.resolve(false)
    return commitPageContent(pageUuid, { pageTitle: v })
  }
  const onBlurLeadSentence = (v: string): Promise<boolean> => {
    updateLeadSentence(v)
    if (!pageUuid) return Promise.resolve(false)
    return commitPageContent(pageUuid, { leadSentence: v })
  }
  const component = useMemo(() => {
    if (!snapshotUuid || !page) return <></>
    if (page.config.type === 'PROGRESS_OVERVIEW') {
      return (
        <ProgressOverview snapshotUuid={snapshotUuid} config={page.config} />
      )
    }
    if (page.config.type === 'PROGRESS_REPORT_BY_PROCESS') {
      return (
        <>
          <ProgressReportByProcess
            key={page.uuid ?? ''}
            editable={!isPreview}
            snapshotUuid={snapshotUuid}
            pageUuid={page.uuid ?? ''}
            config={page.config}
            rows={page.rows}
            updateRow={updateRow}
            applyReorderedRows={applyReorderedRows}
            displayState={displayState}
            updateDisplayState={updateDisplayState}
            callbackWhenStartLoading={showLoading}
            callbackWhenFinishLoading={hideLoading}
            columnStateApplyToken={displayStateRestoreToken}
          />
        </>
      )
    }
    if (page.config.type === 'PROGRESS_REPORT_BY_TEAM') {
      return (
        <ProgressReportByTeam
          key={page.uuid ?? ''}
          editable={!isPreview}
          snapshotUuid={snapshotUuid}
          pageUuid={page.uuid ?? ''}
          config={page.config}
          rows={page.rows}
          updateRow={updateRow}
          applyReorderedRows={applyReorderedRows}
        />
      )
    }
    return <></>
  }, [
    isPreview,
    page,
    snapshotUuid,
    displayState,
    updateDisplayState,
    updateRow,
    applyReorderedRows,
    showLoading,
    hideLoading,
    displayStateRestoreToken,
  ])
  return (
    <PageRoot>
      {page && (
        <PageHeader
          pageTitle={page.pageTitle}
          leadSentence={page.leadSentence}
          onChangePageTitle={updatePageTitle}
          onChangeLeadSentence={updateLeadSentence}
          onBlurPageTitle={onBlurPageTitle}
          onBlurLeadSentence={onBlurLeadSentence}
          editable={!isPreview}
        />
      )}
      {component}
    </PageRoot>
  )
}
